/**
 * Reviewable groups/fields on a counterparty. Unlike
 * `TransactionReviewField` (one entry per scalar/relation field), some
 * counterparty entries represent a GROUP of fields reviewed together as a
 * single unit — "basic info" and "address" each snapshot several columns at
 * once, while "1099 eligible" and "tax classification" are single fields.
 */
export enum CounterpartyReviewField {
    BASIC_INFO = "basicInfo",
    ADDRESS = "address",
    ELIGIBLE_1099 = "eligible1099",
    TAX_CLASSIFICATION = "taxClassification",
    TIN = "tin",
}

/**
 * The fields the generic review endpoints, filters and cells accept. `TIN` is
 * reviewed through the tax ID review endpoints only, which re-check the admin
 * role, the vault flag and the reviewed revision, and never appears in agent
 * tool schemas.
 */
const { TIN: _tin, ...genericReviewFields } = CounterpartyReviewField;
export const GENERIC_COUNTERPARTY_REVIEW_FIELD_ENUM = genericReviewFields;

export type GenericCounterpartyReviewField = Exclude<
    CounterpartyReviewField,
    CounterpartyReviewField.TIN
>;

export const GENERIC_COUNTERPARTY_REVIEW_FIELDS: GenericCounterpartyReviewField[] =
    Object.values(GENERIC_COUNTERPARTY_REVIEW_FIELD_ENUM);

export const COUNTERPARTY_REVIEW_FIELD_LABELS: Record<
    CounterpartyReviewField,
    string
> = {
    [CounterpartyReviewField.BASIC_INFO]: "Basic info",
    [CounterpartyReviewField.ADDRESS]: "Address",
    [CounterpartyReviewField.ELIGIBLE_1099]: "1099 eligible",
    [CounterpartyReviewField.TAX_CLASSIFICATION]: "Tax classification",
    [CounterpartyReviewField.TIN]: "Tax ID",
};

/**
 * Counterparty columns that can be bundled into a review field/group. Kept as a
 * dedicated union so the review services can index a typed counterparty snapshot
 * without casting through `Record<string, unknown>`.
 */
export type CounterpartyReviewColumn =
    | "name"
    | "email"
    | "cc"
    | "bcc"
    | "phoneNumber"
    | "website"
    | "title"
    | "firstName"
    | "middleName"
    | "lastName"
    | "suffix"
    | "street"
    | "street2"
    | "city"
    | "postalCode"
    | "state"
    | "country"
    | "isEligible1099"
    | "federalTaxClassification";

/** Scalar columns bundled under each group-type review field. Single-field entries map to their one column. */
export const COUNTERPARTY_REVIEW_FIELD_COLUMNS: Record<
    CounterpartyReviewField,
    CounterpartyReviewColumn[]
> = {
    [CounterpartyReviewField.BASIC_INFO]: [
        "name",
        "email",
        "cc",
        "bcc",
        "phoneNumber",
        "website",
        "title",
        "firstName",
        "middleName",
        "lastName",
        "suffix",
    ],
    [CounterpartyReviewField.ADDRESS]: [
        "street",
        "street2",
        "city",
        "postalCode",
        "state",
        "country",
    ],
    [CounterpartyReviewField.ELIGIBLE_1099]: ["isEligible1099"],
    [CounterpartyReviewField.TAX_CLASSIFICATION]: ["federalTaxClassification"],
    // History shows review snapshots to every member; the tax ID type stays admin-only.
    [CounterpartyReviewField.TIN]: [],
};

export function isCounterpartyReviewField(
    value: string,
): value is CounterpartyReviewField {
    return Object.prototype.hasOwnProperty.call(
        COUNTERPARTY_REVIEW_FIELD_LABELS,
        value,
    );
}

export function getCounterpartyReviewFieldLabel(field: string): string {
    return isCounterpartyReviewField(field)
        ? COUNTERPARTY_REVIEW_FIELD_LABELS[field]
        : field;
}

export type CounterpartyReviewColumnValue = string | boolean | null | undefined;

function areCounterpartyReviewValuesEqual(
    before: CounterpartyReviewColumnValue,
    update: CounterpartyReviewColumnValue,
): boolean {
    if (typeof before === "boolean" || typeof update === "boolean") {
        return before === update;
    }
    // Treat blank string / undefined the same as null so a full-form save that
    // re-sends empty optional fields does not look like a real change.
    const normalize = (value: string | null | undefined) =>
        value === undefined || value === null || value === "" ? null : value;
    return normalize(before) === normalize(update);
}

/**
 * Returns the review fields/groups that should be invalidated for a counterparty
 * update. Only columns present on `update` are considered — mirrors how
 * transaction field reviews clear based on the applied update payload, so an
 * unrelated group (e.g. basicInfo) is never cleared when only `isEligible1099`
 * changes.
 */
export function getChangedCounterpartyReviewFields(params: {
    before: Partial<
        Record<CounterpartyReviewColumn, CounterpartyReviewColumnValue>
    >;
    update: Partial<
        Record<CounterpartyReviewColumn, CounterpartyReviewColumnValue>
    >;
}): CounterpartyReviewField[] {
    const { before, update } = params;
    return Object.values(CounterpartyReviewField).filter((field) =>
        COUNTERPARTY_REVIEW_FIELD_COLUMNS[field].some((column) => {
            if (!Object.prototype.hasOwnProperty.call(update, column)) {
                return false;
            }
            return !areCounterpartyReviewValuesEqual(
                before[column],
                update[column],
            );
        }),
    );
}

/** Snapshot of the reviewed group's column values at review time. Null when every column was empty (empty reviews are still valid). */
export type CounterpartyFieldReviewSnapshot = Record<
    string,
    string | boolean | null
> | null;

export interface CounterpartyFieldReviewMeta {
    reviewedField?: string;
    count?: number;
    indirect?: boolean;
    memberCount?: number;
    memberIds?: number[];
    fieldValue?: CounterpartyFieldReviewSnapshot;
}

export interface CounterpartyFieldReview {
    id: string;
    counterpartyId: string;
    userId: number;
    field: CounterpartyReviewField;
    createdAt: Date;
}
